import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Chip,
  useTheme,
  alpha,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import {
  Search as SearchIcon,
  ClearAll,
  Warning
  // Print
} from '@mui/icons-material';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';

// Notification interface based on API response
interface Notification {
  NotificationID: number;
  EmailSubject: string;
  EmailID: string;
  SentOn: string;
  EmailStatus: string;
}

// API Response interface
interface NotificationAPIResponse {
  status: string;
  message: string;
  data: {
    TotalRecords: number;
    CurrentPage: number;
    pageSize: number;
    totalPages: number;
    Data: Notification[];
  };
}

interface SearchCriteria {
  emailSubject: string;
  email: string;
  notificationStatus: string;
}

const AuthNotifications: React.FC = () => {
  const theme = useTheme();

  // Search criteria state
  const [searchCriteria, setSearchCriteria] = useState<SearchCriteria>({
    emailSubject: '',
    email: '',
    notificationStatus: 'All'
  });

  // Main data states
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Fetch notifications
  const fetchNotifications = async (
    pageNum: number,
    pageSize: number,
    criteria?: SearchCriteria // Add optional parameter
  ) => {
    setLoading(true);
    try {
      const activeCriteria = criteria || searchCriteria;

      // Only search by Email Subject, Email, and Notification Status
      const queryParams = new URLSearchParams({
        Page: (pageNum + 1).toString(),
        Size: pageSize.toString(),
        ...(activeCriteria.emailSubject.trim() && {
          EmailSubject: activeCriteria.emailSubject.trim()
        }),
        ...(activeCriteria.email.trim() && {
          Email: activeCriteria.email.trim()
        }),
        ...(activeCriteria.notificationStatus !== 'All' && {
          NotificationStatus: activeCriteria.notificationStatus
        })
      });

      const response = (await httpRequest(
        'GET',
        `/auth/admin/vendor/notifications?${queryParams}`
      )) as NotificationAPIResponse;

      if (response.status === 'success') {
        setNotifications(response.data.Data);
        setTotalCount(response.data.TotalRecords);
      } else {
        toast.error(response.message || 'Failed to fetch notifications');
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Failed to fetch notifications. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch and pagination changes
  useEffect(() => {
    fetchNotifications(page, rowsPerPage);
  }, [page, rowsPerPage]);

  // Handle search
  const handleSearch = () => {
    setPage(0);
    fetchNotifications(0, rowsPerPage);
  };

  // Handle clear - Updated to clear contractNo instead of contractNos
  const handleClear = () => {
    const clearedCriteria: SearchCriteria = {
      emailSubject: '',
      email: '',
      notificationStatus: 'All'
    };

    setSearchCriteria(clearedCriteria);
    setPage(0);
    fetchNotifications(0, rowsPerPage, clearedCriteria); // Pass cleared criteria directly
  };

  // Pagination handlers
  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Format date and time
  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (e) {
      return '-';
    }
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'delivered':
        return theme.palette.success.main;
      case 'failed':
        return theme.palette.error.main;
      case 'pending':
        return theme.palette.warning.main;
      default:
        return theme.palette.grey[500];
    }
  };

  return (
    <Box
      sx={{
        p: 3,
        background: alpha(theme.palette.background.default, 0.8),
        minHeight: '100vh'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            color: theme.palette.primary.main,
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: '100%',
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.palette.primary.main
            }
          }}
        >
          Vendor Email Logs
        </Typography>
      </Box>

      {/* Search Filters Card - only Email Subject, Email & Notification Status */}
      <Card
        sx={{
          mb: 4,
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
        }}
      >
        <Box
          sx={{
            p: 2.5,
            background: `linear-gradient(to right, ${alpha(
              theme.palette.primary.main,
              0.05
            )}, ${alpha(theme.palette.primary.main, 0.02)})`,
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
          }}
        >
          <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
            <SearchIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Search Filters
          </Typography>
        </Box>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2.5}>
              {/* Email Subject */}
              <Grid item xs={12} sm={6} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Email Subject"
                  variant="outlined"
                  value={searchCriteria.emailSubject}
                  onChange={(e) =>
                    setSearchCriteria({
                      ...searchCriteria,
                      emailSubject: e.target.value
                    })
                  }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                      height: 40
                    }
                  }}
                />
              </Grid>

              {/* Email */}
              <Grid item xs={12} sm={6} md={6}>
                <TextField
                  fullWidth
                                  size="small"
                  label="Email"
                  variant="outlined"
                  value={searchCriteria.email}
                  onChange={(e) =>
                        setSearchCriteria({
                          ...searchCriteria,
                      email: e.target.value
                    })
                  }
                  sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                          height: 40
                    }
                  }}
                />
              </Grid>

              {/* Third row - Notification Status and Buttons in same row */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2
                  }}
                >
                  <FormControl
                    component="fieldset"
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center'
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        marginRight: 2
                      }}
                    >
                      Notification Status:
                    </Typography>
                    <RadioGroup
                      row
                      value={searchCriteria.notificationStatus}
                      onChange={(e) =>
                        setSearchCriteria({
                          ...searchCriteria,
                          notificationStatus: e.target.value
                        })
                      }
                      sx={{ display: 'flex', gap: 2, flexDirection: 'row' }}
                    >
                      <FormControlLabel
                        value="Delivered"
                        control={<Radio />}
                        label="Delivered"
                      />
                      <FormControlLabel
                        value="Failed"
                        control={<Radio />}
                        label="Failed"
                      />
                      <FormControlLabel
                        value="Pending"
                        control={<Radio />}
                        label="Pending"
                      />
                      <FormControlLabel
                        value="All"
                        control={<Radio />}
                        label="All"
                      />
                    </RadioGroup>
                  </FormControl>

                  {/* Search and Clear buttons on the right */}
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      alignItems: 'center'
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSearch}
                      startIcon={<SearchIcon />}
                      size="small"
                      sx={{
                        borderRadius: 1.5,
                        px: 3,
                        py: 0.8,
                        fontWeight: 'bold',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          boxShadow: '0 4px 10px rgba(0, 0, 0, 0.18)',
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      Search
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleClear}
                      startIcon={<ClearAll />}
                      size="small"
                      sx={{
                        borderRadius: 1.5,
                        px: 3,
                        py: 0.8,
                        fontWeight: 'bold',
                        borderWidth: '1px',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderWidth: '1px',
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.04
                          )
                        }
                      }}
                    >
                      Clear
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
        </CardContent>
      </Card>

      {/* Results Table */}
      <Paper
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
        }}
      >
        <Box
          sx={{
            p: 2.5,
            bgcolor: `linear-gradient(to right, ${alpha(
              theme.palette.primary.main,
              0.05
            )}, ${alpha(theme.palette.primary.main, 0.02)})`,
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
            Logs
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {!loading && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                Total Results: {totalCount}
              </Typography>
            )}
            {/* <Button
              variant="outlined"
              size="small"
              startIcon={<Print />}
              sx={{
                borderRadius: 1.5,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08)
                }
              }}
            >
              Print
            </Button> */}
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table
                sx={{
                  '& .MuiTableCell-head': {
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                    fontWeight: 'bold',
                    color: theme.palette.text.primary,
                    py: 2
                  },
                  '& .MuiTableCell-body': {
                    py: 1.8
                  }
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Email Subject</TableCell>
                    <TableCell>Email/Fax</TableCell>
                    <TableCell>Sent On</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {notifications?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2
                          }}
                        >
                          <Warning
                            sx={{
                              fontSize: 40,
                              color: alpha(theme.palette.text.secondary, 0.6)
                            }}
                          />
                          <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ fontWeight: 500 }}
                          >
                            No notifications found. Try adjusting your search
                            criteria.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    notifications?.map((notification) => (
                      <TableRow
                        key={notification.NotificationID}
                        hover
                        sx={{
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.03)
                          },
                          transition: 'background-color 0.2s ease',
                          '&:nth-of-type(even)': {
                            bgcolor: alpha(
                              theme.palette.background.default,
                              0.4
                            )
                          }
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>
                          {notification.EmailSubject}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 400 }}>
                          {notification.EmailID}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.875rem' }}>
                          {formatDateTime(notification.SentOn)}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={notification.EmailStatus}
                            size="small"
                            sx={{
                              bgcolor: alpha(
                                getStatusColor(notification.EmailStatus),
                                0.1
                              ),
                              color: getStatusColor(notification.EmailStatus),
                              fontWeight: 'bold',
                              borderRadius: 1
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box
              sx={{
                borderTop: `1px solid ${alpha(
                  theme.palette.primary.main,
                  0.1
                )}`,
                bgcolor: alpha(theme.palette.background.paper, 0.5)
              }}
            >
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
                    {
                      fontWeight: 500
                    },
                  '& .MuiTablePagination-select': {
                    borderRadius: 1.5,
                    '&:focus': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.05)
                    }
                  },
                  '& .MuiTablePagination-actions': {
                    '& .MuiIconButton-root': {
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1)
                      }
                    }
                  }
                }}
              />
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default AuthNotifications;
